import { definePreset } from '@primeuix/themes';
import Preset from '@primeuix/themes/lara';
import { Device } from './config/device';

// before Safari 26 (iOS 26), which includes the old iPads; 0 means not Safari
const safari = Device.safariVersion();
const oldSafari = safari > 0 && safari < 26;

export default definePreset(Preset, {
    /*
     * Lara sizes its corners in px (xs 2 .. xl 12), so they were the one thing that did
     * not follow the root font-size -- on a zoomed iPad a dialog kept a 12px CSS corner
     * that the browser then magnified, which is why dialogs looked overly round there.
     * Kept in rem and converted at the 14px default, so they stay the same size at
     * normal scale: 12 / 14 rem for the largest.
     */
    primitive: {
        borderRadius: {
            none: '0',
            xs: '0.1429rem',
            sm: '0.2857rem',
            md: '0.4286rem',
            lg: '0.5714rem',
            xl: '0.8571rem',
        },
    },
    theme: {
        options: {
            darkModeSelector: '.dark',
        },
    },
    semantic: {
        // The only px value left in Lara's tokens: the gap between an overlay and the
        // element it hangs off. 2 / 14 rem keeps it at 2px in normal use.
        anchorGutter: '0.1429rem',
        // The mask fades in by animating its background, a full-screen repaint every frame
        // over table and video, which costs frames on an old iPad. There it shows at once.
        ...(oldSafari ? { mask: { transitionDuration: '0s' } } : {}),
        primary: {
            50: '{emerald.50}',
            100: '{emerald.100}',
            200: '{emerald.200}',
            300: '{emerald.300}',
            400: '{emerald.400}',
            500: '{emerald.500}',
            600: '{emerald.600}',
            700: '{emerald.700}',
            800: '{emerald.800}',
            900: '{emerald.900}',
            950: '{emerald.950}'
        },
        colorScheme: {
            light: {
                //         primary: {
                //             color: '{zinc.950}',
                //         }
                text: {
                    color: 'black',
                },
                hilight: {
                    color: 'black',
                    background: 'yellow',
                },
            }
        },
        overlay: {
            modal: {
                // --p-overlay-modal-padding: .7rem;
                padding: '.7em',
                // als ik onderstaande toevoeg dan worden de buttons niet meer goed getoond: 
                // geen shaduw meer en geen kleur.
                // shadow: '0 10px 20px 0px rgba(0,0,0,0.5),0 8px 20px -6px rgba(0,0,0,0.3)'
            }
        },
        form: {
            field: {
                padding: {
                    // --p-form-field-padding-x: 0.25rem;
                    // --p-form-field-padding-y: 0.25rem;
                    x: '0.6em',
                    y: '0.3em'
                }
            }
        },
        // icon: {
        // size: '1em',
        // },
    },

    components: {
        menubar: {
            root: {
                padding: '0.1em'
            },
            item: {
                // padding: Device.isTouch ? '1em' : '0.6em'
            },
            submenu: {
                // padding: '0.2em'
            },
            baseItem: { // top layer
                padding: Device.isTouch ? '0.6em 0.8em' : '0.6em'
            }
        },
        button: {
            colorScheme: {
                light: {
                    root: {
                        // primary: {
                        //     background: '{primary.600}',
                        //     hoverBackground: '{primary.700}',
                        //     borderColor: '{primary.600}',
                        //     hoverBorderColor: '{primary.700}'
                        // },
                        secondary: {
                            background: '{surface.100}',
                            hoverBackground: '{surface.200}',
                            borderColor: '{surface.200}',
                            hoverBorderColor: '{primary.300}'
                        }
                    },
                    outlined: {
                        primary: {
                            borderColor: '{primary.400}',
                            color: '{primary.600}',
                        }
                    },
                },
                dark: {
                    root: {
                        secondary: {
                            // hoverBorderColor: '{primary.700}'
                        }
                    }

                },
            },
            root: {
                // secondary: {
                //     background: '{surface.500}'
                // },
                paddingX: '1.0rem',
                paddingY: '0.625rem',
                iconOnlyWidth: '3rem',
            },
        },
        colorpicker: {
            preview: {
                width: '2.5em',
            },
        },
        contextmenu: {
            item: {
                padding: Device.isTouch ? '1em' : '0.5em',
            }
        },
        datatable: {
            bodyCell: {
                padding: '0.3rem 0.5rem',
                lg: {
                    padding: '0.7em 0.5em',
                },
                sm: {
                    padding: '0.2em 0.3em',
                }
            },
            headerCell: {
                padding: '0.5rem 0.5rem',
                lg: {
                    padding: '1em 0.5em',
                },
                sm: {
                    padding: '0.4em 0.3em',
                },
                selectedColor: 'var(--p-text-color)',
            },
            row: {
                selectedColor: 'var(--p-text-color)',
                selectedBackground: 'var(--color-selected-background)',
                // background: 'color-mix(in srgb, var(--p-primary-400), transparent 70%)'
                // color-mix is way to new... safari 16+
            },
        },
        panel: {
            header: {
                padding: "0.7em 1em"
            },
            toggleableHeader: {
                padding: "0.7em 1em"
            }
        },
        tabs: {
            activeBar: {
                background: 'none',
            }
        },
        tabpanel: {
            background: '{surface.50}',
        },
        select: {
            root: {
                paddingY: '0.5em',
            },
            colorScheme: {
                light: {
                    overlay: {
                        borderColor: '{surface.300}',
                    },
                    option: {
                        selectedColor: '{surface.1000}',
                        selectedBackground: '{primary.200}',
                    }
                },
                dark: {
                    overlay: {
                        borderColor: '{surface.700}',
                    },
                    option: {
                        selectedColor: '{surface.0}',
                        selectedBackground: '{primary.800}',
                    }
                }
            },
            dropdown: {
                width: '2rem'
            }
        },
        radiobutton: {
            icon: {
                size: '0.5rem',
                // checkedColor: '{primary.400}',
            }
        },

        togglebutton: {
            colorScheme: {
                light: {
                    root: {
                        checkedBackground: '{primary.200}',
                        checkedColor: '{surface.900}',
                    },
                    content: {
                        checkedBackground: '{primary.200}',
                        // checkedShadow: '3px 2px 2px 1px rgba(0,0,0,0.05)'
                    },
                },
                dark: {
                    root: {
                        checkedBackground: '{primary.700}',
                        checkedColor: '{surface.000}',
                    },
                    content: {
                        checkedBackground: '{primary.700}',
                    },
                }
            },
            content: {
                // --p-togglebutton-padding: 0.625rem 1rem;
                // padding: 'var(--p-button-padding-y) var(--p-button-padding-x)',
            },
            root: {
                fontWeight: 'inherit',
                // padding: '0.5em 1em'
            }
        }
    }
})